import { Injectable } from '@angular/core';

import { StorageToken } from '../../../shared/configs/storage-token.config';
import { UserPermission } from '../../../shared/configs/user-permission.config';
import { UserRole } from '../../../shared/configs/user-role.config';
import { MasterDataService } from '../http/master-data/master-data.service';
import { StorageService } from '../storage/storage.service';

import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  /** Subject used for holding the user details decoded from JWT token.
   * Use userDetails (getter) to get the user details.
   * Use getUserDetails$() to get the observable to subscribe to.
   * Use setUserDetails$() to set the value to the subject.
   */
  private userDetails$: BehaviorSubject<any>;
  private userDetailsData$: BehaviorSubject<any>;
  private toDoDetails$: BehaviorSubject<any>;
  private todoOpen$: BehaviorSubject<Boolean>;

  constructor(private storageService: StorageService, private masterService: MasterDataService) {
    this.userDetails$ = new BehaviorSubject<any>(null);
    this.userDetailsData$ = new BehaviorSubject<any>(null);
    this.toDoDetails$ = new BehaviorSubject<any>(null);
    this.todoOpen$ = new BehaviorSubject<Boolean>(false);
  }

  /** For directly getting the user details value */
  get userDetails() {
    return this.userDetails$.value;
  }

  get toDoDetails() {
    return this.toDoDetails$.value;
  }

  get isTodoDialogOpened() {
    return this.todoOpen$.value;
  }

  /** For getting the user details as an observable to subscribe to */
  getUserDetails$(): Observable<any> {
    return this.userDetails$.asObservable();
  }

  /** For getting the user details as an observable to subscribe to (view staff api)*/
  getUserDetailsData$(): Observable<any> {
    return this.userDetailsData$.asObservable();
  }

  getToDoDetails$(): Observable<any> {
    return this.toDoDetails$.asObservable();
  }

  /** For getting the user roles from the user details */
  getUserRoles(): UserRole[] {
    const keycloakRoles = this.userDetails?.resource_access?.['nephroplus-bo']?.roles;
    if (keycloakRoles != null) return keycloakRoles;

    const cognitoGroups: string[] = this.userDetails?.['cognito:groups'] ?? [];
    const knownRoles = new Set<string>(Object.values(UserRole));
    return cognitoGroups.map(group => {
      if (knownRoles.has(group)) return group as UserRole;
      // Cognito groups use underscores; most UserRole values use spaces
      const spaced = group.replace(/_/g, ' ');
      return (knownRoles.has(spaced) ? spaced : group) as UserRole;
    });
  }

  /** For getting the user permissions from the user details */
  getUserPermissions(): UserPermission[] {
    return this.userDetails?.authorization?.permissions?.[0]?.scopes;
  }

  /** For checking if user has at least one of the required roles
   * @param requiredRolesList - list of roles to be checked
   */
  isUserAllowedByRole(requiredRolesList: UserRole[]): boolean {
    if (requiredRolesList == null || requiredRolesList.length === 0) {
      return true;
    }

    const roles = this.getUserRoles();
    if (!roles) return true;
    return roles.some(userRole => requiredRolesList.includes(userRole));
  }

  /** For checking if user has at least one of the required permissions
   * @param requiredPermissionList - list of permissions to be checked
   */
  isUserAllowedByPermission(requiredPermissionList: UserPermission[]): boolean {
    if (requiredPermissionList == null || requiredPermissionList.length === 0) {
      return true;
    }

    let fetchIsMasqueraded = this.storageService.getCookie(StorageToken.masquerade);

    let isMasqueraded = fetchIsMasqueraded ? atob(this.storageService.getCookie(StorageToken.masquerade)) : 'false';

    if (isMasqueraded === 'true') {
      const masPermission = this.storageService.getFromLocalStorage(StorageToken.masqueradePermissions);
      const permissionList: string[] = masPermission ? JSON.parse(atob(masPermission)) : [];
      const isPermissionAllowed = permissionList.some((userPermission: any) =>
        requiredPermissionList.includes(userPermission)
      );

      return isPermissionAllowed;
    }

    const permissions = this.getUserPermissions();
    if (!permissions) return true;
    return permissions.some(userPermission => requiredPermissionList.includes(userPermission));
  }

  /** For setting a value to the userDetails$ subject
   * @param userDetails - user details
   */
  setUserDetails$(userDetails: any): void {
    this.userDetails$.next(userDetails);
  }

  setUserPermissions(scopes: string[]): void {
    const userDetails = this.userDetails$.value;
    if (userDetails) {
      userDetails.authorization = { permissions: [{ scopes: scopes || [] }] };
      this.userDetails$.next(userDetails);
    }
  }

  /** For setting a value to the userDetails$ subject(view staff api)
   * @param userDetailsData - user details
   */
  setUserDetailsData$(userDetailsData: any): void {
    this.userDetailsData$.next(userDetailsData);
  }

  setToDoDetails$(toDoDetails : any): void {
    this.toDoDetails$.next(toDoDetails);
  }

  setTODODialogState(state: boolean): void {
    this.todoOpen$.next(state);
  }

  setToDoOpen(isOpen: boolean): void {
    if(!isOpen){
    this.masterService
    ?.fetchToDoValidation()
    ?.pipe()
    .subscribe((res: any) => {
      let isPending = false;
      if (res?.data?.summary.find((obj: any) => obj.count > 0) && !res?.data?.bypass) {
        isPending = true;
      }
      res.data.isPending = isPending;
      res.data.bypassCount = res?.data?.count;
      this.setToDoDetails$(res?.data);
    });
  }
  this.todoOpen$.next(isOpen);
}
}
